/**
 * Lift2Fit — the application router.
 *
 * Mounted by the landing page alongside it: for the landing path (`/`) this
 * component renders nothing, so the marketing page keeps the viewport. Every
 * other path takes over with the post-login app shell.
 *
 * Routes: /login · /signup · /dashboard (lifter) · /grid (the lifter's own log) ·
 * /programme (the lifter's own plan) · /summary · /goals · /coach (the coach's
 * authoring desk) · /plan/new · /plan/:id · /profile · /crm/signup (business
 * sign-up) · /crm/login (business login) · /crm/* (the coaching workspace:
 * summary, calendar, clients, client, payments, todo, insights) · /admin/* (the
 * founder's surface: overview, accounts, account, businesses, revenue, audit) ·
 * * → friendly not-found. The phase-1 /clients routes redirect into the
 * workspace. The legacy /trainer route is gone: `trainer` is an internal
 * database role, never a product surface.
 */
import './theme.css';
import { useEffect } from 'react';
import { useSession } from '@/lib/useSession';
import { AppShell, LifterInterfaceRequired, RoleRequired, SignInRequired } from './AppShell';
import { EmptyState } from './components';
import { Link, matchRoute, navigate, useRoute, type RouteMatch } from './router';
import { LoginScreen } from './screens/LoginScreen';
import { LifterDashboard } from './screens/LifterDashboard';
import { MyGridScreen } from './screens/MyGridScreen';
import { CoachSessionScreen, LifterSessionScreen } from './session-detail';
import { ProgrammeScreen } from './screens/ProgrammeScreen';
import { ProgrammeAllScreen } from './screens/ProgrammeAllScreen';
import { BlockSummaryScreen } from './screens/BlockSummaryScreen';
import { GoalsScreen } from './screens/GoalsScreen';
import { MyCoachScreen } from './my-coach/MyCoachScreen';
import { ScheduleEditorScreen } from './schedule';
import { CoachDashboard } from './screens/CoachDashboard';
import { ProfileScreen } from './screens/ProfileScreen';
import { PlanWizard } from './screens/PlanWizard';
import { PlanResultScreen } from './screens/PlanResultScreen';
import { CoachJoinScreen } from './coach';
import { CrmSummaryScreen } from './crm/CrmSummaryScreen';
import { CrmSignupScreen } from './crm/CrmSignupScreen';
import { CrmCalendarScreen } from './crm/CrmCalendarScreen';
import { CrmClientsScreen } from './crm/CrmClientsScreen';
import { CrmJoinTeamScreen } from './crm/CrmJoinTeamScreen';
import { CrmTeamScreen } from './crm/CrmTeamScreen';
import { ChatWorkspace } from './crm/ChatWorkspace';
import { HelpScreen } from './screens/HelpScreen';
import { CrmPacksScreen } from './crm/CrmPacksScreen';
import { CrmProfileScreen } from './crm/CrmProfileScreen';
import { CrmTodoScreen } from './crm/CrmTodoScreen';
import { CrmClientScreen } from './crm/CrmClientScreen';
import { CrmClientProgrammesScreen } from './crm/CrmClientProgrammesScreen';
import { CrmPaymentsScreen } from './crm/CrmPaymentsScreen';
import { CrmInsightsScreen } from './crm/CrmInsightsScreen';
import { AdminOverviewScreen } from './admin/AdminOverviewScreen';
import { AdminAccountsScreen } from './admin/AdminAccountsScreen';
import { AdminAccountScreen } from './admin/AdminAccountScreen';
import { AdminBusinessesScreen } from './admin/AdminBusinessesScreen';
import { AdminLiftersScreen } from './admin/AdminLiftersScreen';
import { AdminRevenueScreen } from './admin/AdminRevenueScreen';
import { AdminPlansScreen } from './admin/AdminPlansScreen';
import { AdminOffersScreen } from './admin/AdminOffersScreen';
import { AdminInvitationsScreen } from './admin/AdminInvitationsScreen';
import { AdminSystemScreen } from './admin/AdminSystemScreen';
import { AdminAuditScreen } from './admin/AdminAuditScreen';
import { AdminJoinScreen } from './admin/AdminJoinScreen';
import { AdminChatScreen } from './admin/AdminChatScreen';
import { AdminProfileScreen } from './admin/AdminProfileScreen';
import { AdminStaffScreen } from './admin/AdminStaffScreen';
import { StaffScreen } from './staff/StaffScreen';
import { LegalIndex, LegalScreen } from './legal/LegalScreen';
import { legalDocIdForSlug } from './legal/policy';

/** Adopt the folded-in phase-1 link, then render the workspace route. */
function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to]);
  return null;
}

function NotFound() {
  return (
    <EmptyState
      title="That page does not exist"
      body="The link may be old, or the screen may have moved. Your log is untouched — pick a surface below."
      action={
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard"
            className="inline-flex h-11 items-center bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
          >
            Back to the dashboard
          </Link>
          <Link
            to="/plan/new"
            className="inline-flex h-11 items-center border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Build a plan
          </Link>
        </div>
      }
    />
  );
}

function RouteView({ match }: { match: RouteMatch }) {
  const session = useSession();

  if (match.name === 'not-found') return <NotFound />;

  if (!session.isAuthenticated) return <SignInRequired />;

  // The lifter interface is a paid-plan entitlement for a coach (0021). A
  // free-tier coach who deep-links a lifter TRAINING route gets the reason and
  // the upgrade path, never the lifter screens. `/my-coach` is deliberately not
  // in this set: it is the coaching-RELATIONSHIP surface (accepting an invite,
  // managing the link, booking with your own coach), which a person who gained
  // the coach role by accepting an invite must still reach even on the free
  // plan. The API enforces the same split on the own-training endpoints.
  const lifterRoutes = new Set([
    'dashboard',
    'grid',
    'grid-session',
    'programme',
    'programme-all',
    'summary',
    'goals',
    'schedule',
    'plan-new',
    'plan',
    'profile',
  ]);
  if (session.user?.lifterInterface === false && lifterRoutes.has(match.name)) {
    return <LifterInterfaceRequired />;
  }

  switch (match.name) {
    case 'dashboard':
      return <LifterDashboard />;
    case 'grid':
      return <MyGridScreen />;
    case 'grid-session':
      return <LifterSessionScreen sessionId={match.param ?? ''} />;
    case 'programme':
      return <ProgrammeScreen />;
    // Every block with the detail the inline list sheds (C). The coach reuses the
    // same page read-only with `?clientId=`; the query is read by the screen.
    case 'programme-all':
      return <ProgrammeAllScreen />;
    case 'summary':
      return <BlockSummaryScreen />;
    case 'goals':
      return <GoalsScreen />;
    // The lifter's session schedule: the "when" layer over the undated block.
    case 'schedule':
      return <ScheduleEditorScreen />;
    // The lifter's own coaching surface: a lifter route, present in the lifter
    // shell for every account (a coach viewing their own training included).
    case 'my-coach':
      return <MyCoachScreen />;
    // The reminder's booking action lands on the Booking tab directly.
    case 'my-coach-booking':
      return <MyCoachScreen initialTab="booking" />;
    case 'coach':
      return session.hasRole('coach') ? <CoachDashboard /> : <RoleRequired role="coach" />;
    // The coaching workspace. Only an account holding the coach role may enter,
    // whether from the nav or by typing the route.
    case 'crm-summary':
      return session.hasRole('coach') ? <CrmSummaryScreen /> : <RoleRequired role="coach" />;
    case 'crm-calendar':
      return session.hasRole('coach') ? <CrmCalendarScreen /> : <RoleRequired role="coach" />;
    case 'crm-clients':
      return session.hasRole('coach') ? <CrmClientsScreen /> : <RoleRequired role="coach" />;
    case 'crm-team':
      return session.hasRole('coach') ? <CrmTeamScreen /> : <RoleRequired role="coach" />;
    // 0028 IA: chat/prospects moved out of the CRM shell into `#/chat`. The old
    // in-shell routes stay as redirects so an old bookmark still lands.
    case 'crm-chat':
    case 'crm-prospects':
      return <Redirect to="/chat" />;
    case 'chat':
      // The workspace is caller-scoped and serves BOTH roles; a plain lifter
      // sees their own coaches, a coach sees their clients.
      return session.hasRole('coach') || session.hasRole('lifter') ? (
        <ChatWorkspace />
      ) : (
        <RoleRequired role="coach" />
      );
    case 'crm-packs':
      return session.hasRole('coach') ? <CrmPacksScreen /> : <RoleRequired role="coach" />;
    case 'crm-profile':
      return session.hasRole('coach') ? <CrmProfileScreen /> : <RoleRequired role="coach" />;
    case 'crm-todo':
      return session.hasRole('coach') ? <CrmTodoScreen /> : <RoleRequired role="coach" />;
    case 'crm-client':
      return session.hasRole('coach') ? (
        <CrmClientScreen clientId={match.param ?? ''} />
      ) : (
        <RoleRequired role="coach" />
      );
    // A block the coach opened from All Programmes: the client page in edit
    // mode for that block, including one that is not currently active.
    case 'crm-client-block':
      return session.hasRole('coach') ? (
        <CrmClientScreen clientId={match.param ?? ''} planId={match.param2} />
      ) : (
        <RoleRequired role="coach" />
      );
    // All Programmes for one client: the full list the client page links to.
    case 'crm-client-programmes':
      return session.hasRole('coach') ? (
        <CrmClientProgrammesScreen clientId={match.param ?? ''} />
      ) : (
        <RoleRequired role="coach" />
      );
    // A client's completed session, opened from their history. The coach layer
    // (flags and highlighting) is on; the lifter's own view never renders it.
    case 'crm-client-session':
      return session.hasRole('coach') ? (
        <CoachSessionScreen clientId={match.param ?? ''} sessionId={match.param2 ?? ''} />
      ) : (
        <RoleRequired role="coach" />
      );
    case 'crm-payments':
      return session.hasRole('coach') ? <CrmPaymentsScreen /> : <RoleRequired role="coach" />;
    case 'crm-insights':
      return session.hasRole('coach') ? <CrmInsightsScreen /> : <RoleRequired role="coach" />;
    // The founder's admin surface. The admin role is never self-serve, so a
    // non-admin sees only the refusal panel — never a way to grant it.
    case 'admin-overview':
      return session.hasRole('admin') ? <AdminOverviewScreen /> : <RoleRequired role="admin" />;
    case 'admin-lifters':
      return session.hasRole('admin') ? <AdminLiftersScreen /> : <RoleRequired role="admin" />;
    case 'admin-accounts':
      return session.hasRole('admin') ? <AdminAccountsScreen /> : <RoleRequired role="admin" />;
    case 'admin-account':
      return session.hasRole('admin') ? (
        <AdminAccountScreen userId={match.param ?? ''} />
      ) : (
        <RoleRequired role="admin" />
      );
    case 'admin-businesses':
      return session.hasRole('admin') ? <AdminBusinessesScreen /> : <RoleRequired role="admin" />;
    case 'admin-revenue':
      return session.hasRole('admin') ? <AdminRevenueScreen /> : <RoleRequired role="admin" />;
    case 'admin-plans':
      return session.hasRole('admin') ? <AdminPlansScreen /> : <RoleRequired role="admin" />;
    case 'admin-offers':
      return session.hasRole('admin') ? <AdminOffersScreen /> : <RoleRequired role="admin" />;
    case 'admin-invitations':
      return session.hasRole('admin') ? <AdminInvitationsScreen /> : <RoleRequired role="admin" />;
    case 'admin-system':
      return session.hasRole('admin') ? <AdminSystemScreen /> : <RoleRequired role="admin" />;
    case 'admin-audit':
      return session.hasRole('admin') ? <AdminAuditScreen /> : <RoleRequired role="admin" />;
    // The platform's own chat workspace: every account's Help thread.
    // Rendered standalone (see the wrapper) so it is a chat, not a portal page.
    case 'admin-chat':
      return session.hasRole('admin') ? <AdminChatScreen /> : <RoleRequired role="admin" />;
    // The operator's OWN account page. Separate from `/profile`, which belongs
    // to the lifter product and offers training fields an admin has no use for.
    case 'admin-profile':
      return session.hasRole('admin') ? <AdminProfileScreen /> : <RoleRequired role="admin" />;
    // 0038 — the support team and its desk. The desk refuses politely for an
    // account that is not on the team (the API is the authority).
    case 'admin-staff':
      return session.hasRole('admin') ? <AdminStaffScreen /> : <RoleRequired role="admin" />;
    case 'staff':
      return <StaffScreen />;
    // Phase 1's client routes fold into the workspace.
    case 'legacy-clients':
      return <Redirect to="/crm/clients" />;
    case 'legacy-client':
      return <Redirect to={`/crm/clients/${encodeURIComponent(match.param ?? '')}`} />;
    case 'plan-new':
      return <PlanWizard />;
    case 'plan':
      return <PlanResultScreen planId={match.param ?? ''} />;
    case 'help':
      // The screen refuses politely on its own when nobody is signed in.
      return <HelpScreen />;
    case 'profile':
      return <ProfileScreen />;
    default:
      return <NotFound />;
  }
}

export function AppRouter() {
  const path = useRoute();
  const match = matchRoute(path);

  // The landing page owns `/` — stay out of its way entirely.
  if (match.name === 'landing') return null;

  // Public auth routes render without the app shell.
  if (match.name === 'login' || match.name === 'signup') {
    return (
      <div className="l2f-app-root">
        <LoginScreen mode={match.name} />
      </div>
    );
  }

  // The public legal documents. Deliberately outside the auth gate: consent
  // happens before an account exists, so a signed-out visitor must be able to
  // read exactly what they are being asked to accept.
  if (match.name === 'legal-index') {
    return (
      <div className="l2f-app-root">
        <LegalIndex />
      </div>
    );
  }
  if (match.name === 'legal') {
    const docId = legalDocIdForSlug(match.param);
    if (docId) {
      return (
        <div className="l2f-app-root">
          <LegalScreen docId={docId} />
        </div>
      );
    }
    // An unknown legal slug falls back to the index rather than a sign-in wall.
    return (
      <div className="l2f-app-root">
        <LegalIndex />
      </div>
    );
  }

  // The CRM's own front door: a coaching business signs up signed out and lands
  // in the workspace. Deliberately public, like /login and /signup.
  if (match.name === 'crm-signup') {
    return (
      <div className="l2f-app-root">
        <CrmSignupScreen />
      </div>
    );
  }

  // The business login. The same credentials form as `/login`, rendered with
  // workspace copy and no lifter demo switcher — the door the CRM page's Log in
  // control points at, so a coach is never dropped on the lifter login.
  if (match.name === 'crm-login') {
    return (
      <div className="l2f-app-root">
        <LoginScreen mode="login" audience="crm" />
      </div>
    );
  }

  // The internal-coach invitation link (0033) is public for the same reason: the
  // invited coach may have no account yet, so the page reads the invitation
  // signed out and offers sign-in or sign-up, both returning here.
  if (match.name === 'crm-join-team') {
    return <CrmJoinTeamScreen />;
  }

  // The share link behind a coach invite is deliberately public: the invitee
  // may have no account yet, and redeeming the code is how one is created.
  if (match.name === 'coach-join') {
    return (
      <div className="l2f-app-root">
        <CoachJoinScreen />
      </div>
    );
  }

  // The admin's "free group" invitation link is public for the same reason:
  // the invitee creates their profile through the registration path here.
  if (match.name === 'join') {
    return <AdminJoinScreen code={match.param ?? ''} />;
  }

  // The standalone chat workspaces deliberately render WITHOUT the AppShell:
  // they are their own small apps (no CRM tabs, no coach nav, no admin portal
  // tabs), opened in a new window and deep-linked. `RouteView` still applies the
  // auth/role guard.
  if (match.name === 'chat' || match.name === 'admin-chat' || match.name === 'staff') {
    return (
      <div className="l2f-app-root">
        <RouteView match={match} />
      </div>
    );
  }

  return (
    <div className="l2f-app-root">
      <AppShell>
        <RouteView match={match} />
      </AppShell>
    </div>
  );
}

export { Link, navigate, useRoute } from './router';
